"use client";

import { useParams, Link } from "react-router-dom";
import { ArrowLeft, MapPin, CalendarDays, Building2, FileText, User, Mail, Phone, CheckCircle } from "lucide-react";
import { getApplicationStatus } from "../../utils/applicationStatus";
import useApplicationDetails from "../../hooks/useApplicationDetails";
import {
  formatEmploymentType,
  formatWorkplaceType,
} from "@/modules/jobs/utils/jobFormatter";
export default function ApplicationDetails() {
  const { id } = useParams();
  const { application, loading, error } = useApplicationDetails(id);

  if (loading) {
    return (
      <div className="flex min-h-[300px] items-center justify-center">
        <p className="text-slate-500">Loading application details...</p>
      </div>
    );
  }

  if (error || !application) {
    return (
      <div className="rounded-xl bg-white p-10 text-center">
        <h2 className="text-lg font-semibold">{error || "Application not found"}</h2>
        <Link to="/candidate/applications" className="mt-4 inline-block text-blue-600">
          Back to Applications
        </Link>
      </div>
    );
  }

  const job = application.job;
  const statusText = getApplicationStatus(application.status) || "Unknown";

  return (
    <div className="space-y-6">
      <Link to="/candidate/applications" className="flex w-fit items-center gap-2 text-sm font-medium text-blue-600">
        <ArrowLeft size={16} />
        Back to Applications
      </Link>

      <div className="rounded-2xl border bg-white p-6">
        <h1 className="text-2xl font-bold text-slate-900">{job?.title}</h1>

        <div className="mt-4 flex flex-wrap gap-5 text-sm text-slate-500">
          <span className="flex items-center gap-2">
            <Building2 size={16} />
            {job?.company || "Company"}
          </span>
          <span className="flex items-center gap-2">
            <MapPin size={16} />
            {job?.location}
          </span>
          <span className="flex items-center gap-2">
            <CalendarDays size={16} />
            Applied: {application.createdAt ? new Date(application.createdAt).toLocaleDateString() : "-"}
          </span>
        </div>

        <div className="mt-5 flex flex-wrap gap-3">
          <span className="rounded-full bg-blue-50 px-4 py-2 text-sm font-medium text-blue-700">
            {job?.employmentType
              ? formatEmploymentType(job.employmentType)
              : "-"}
          </span>
          <span className="rounded-full bg-slate-100 px-4 py-2 text-sm font-bold text-slate-700">
            {job?.workplaceType
              ? formatWorkplaceType(job.workplaceType)
              : "-"}
          </span>          <span className="rounded-full bg-green-50 px-4 py-2 text-sm font-medium text-green-700">{statusText}</span>
        </div>
      </div>

      <div className="rounded-2xl border bg-white p-6">
        <div className="flex items-center gap-2">
          <FileText size={20} />
          <h2 className="text-lg font-bold">Job Description</h2>
        </div>
        <p className="mt-4 whitespace-pre-line leading-7 text-slate-600">{job?.description || "-"}</p>
      </div>

      {job?.aboutRole && (
        <div className="rounded-2xl border bg-white p-6">
          <h2 className="text-lg font-bold">About Role</h2>
          <p className="mt-4 whitespace-pre-line leading-7 text-slate-600">{job.aboutRole}</p>
        </div>
      )}

      <div className="rounded-2xl border bg-white p-6">
        <h2 className="text-lg font-bold">Responsibilities</h2>
        <ul className="mt-4 space-y-2">
          {job?.responsibilities?.map((item: any, index: any) => (
            <li key={index} className="flex gap-2 text-slate-600">
              <CheckCircle size={18} className="mt-1 text-green-600" />
              {item}
            </li>
          ))}
        </ul>
      </div>

      <div className="rounded-2xl border bg-white p-6">
        <h2 className="text-lg font-bold">Requirements</h2>
        <ul className="mt-4 space-y-2">
          {job?.requirements?.map((item: any, index: any) => (
            <li key={index} className="flex gap-2 text-slate-600">
              • {item}
            </li>
          ))}
        </ul>
      </div>

      <div className="rounded-2xl border bg-white p-6">
        <h2 className="text-lg font-bold">Job Information</h2>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <div className="rounded-xl bg-slate-50 p-4">
            <p className="text-xs text-slate-500">Department</p>
            <p className="font-semibold">{job?.department || "-"}</p>
          </div>
          <div className="rounded-xl bg-slate-50 p-4">
            <p className="text-xs text-slate-500">Experience</p>
            <p className="font-semibold">
              {job?.minimumExperience} - {job?.maximumExperience || "Any"} Years
            </p>
          </div>
          <div className="rounded-xl bg-slate-50 p-4">
            <p className="text-xs text-slate-500">Skills</p>
            <p className="font-semibold">{job?.skills?.join(", ") || "-"}</p>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border bg-white p-6">
        <div className="flex items-center gap-2">
          <User size={20} />
          <h2 className="text-lg font-bold">Submitted Profile</h2>
        </div>

        <div className="mt-5 grid gap-5 sm:grid-cols-2">
          <div>
            <p className="text-xs text-slate-500">Name</p>
            <p className="font-medium">{application.candidate?.name || "-"}</p>
          </div>
          <div>
            <p className="text-xs text-slate-500">Email</p>
            <p className="flex items-center gap-2 font-medium">
              <Mail size={15} />
              {application.candidate?.email || "-"}
            </p>
          </div>
          <div>
            <p className="text-xs text-slate-500">Phone</p>
            <p className="flex items-center gap-2 font-medium">
              <Phone size={15} />
              {application.candidate?.phone || "-"}
            </p>
          </div>
          <div>
            <p className="text-xs text-slate-500">Experience</p>
            <p className="font-medium">{application.candidate?.totalExperienceMonths || "-"}</p>
          </div>
        </div>
      </div>
    </div>
  );
}